import React, { useEffect, useState } from "react";
import moment from "moment";
import api from "../../api";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LIMIT = 10;

const NumberPlate = ({ number }) => (
  <div style={{ display: "inline-block", background: "#fff", border: "2.5px solid #222", borderRadius: 6, overflow: "hidden", minWidth: 120, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}>
    <div style={{ background: "repeating-linear-gradient(90deg, #c0392b 0px, #c0392b 10px, #e74c3c 10px, #e74c3c 14px, #c0392b 14px, #c0392b 24px)", height: 8, width: "100%" }} />
    <div style={{ padding: "4px 10px 2px", textAlign: "center", fontWeight: 900, fontSize: 15, letterSpacing: 3, color: "#111", fontFamily: "'Courier New', monospace", textTransform: "uppercase" }}>
      {number || "—"}
    </div>
    <div style={{ textAlign: "center", fontSize: 8, fontWeight: 700, letterSpacing: 4, color: "#444", paddingBottom: 4, textTransform: "uppercase" }}>
      SINDH
    </div>
  </div>
);

const BookingStats = ({ stats }) => {
  const cards = [
    { label: "Today's Bookings", value: stats.today, icon: "fa-calendar-day", bg: "#eff6ff", color: "#1d4ed8", iconBg: "#dbeafe" },
    { label: "Last 7 Days", value: stats.week, icon: "fa-calendar-week", bg: "#f5f3ff", color: "#7c3aed", iconBg: "#ede9fe" },
    { label: "This Month", value: stats.month, icon: "fa-calendar", bg: "#fff7ed", color: "#c2410c", iconBg: "#ffedd5" },
    { label: "Total Bookings", value: stats.total, icon: "fa-list", bg: "#f8fafc", color: "#475569", iconBg: "#e2e8f0" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 20, marginTop: 4 }}>
      {cards.map((card, i) => (
        <div key={i} style={{ background: card.bg, borderRadius: 10, padding: "14px 16px", border: `1px solid ${card.iconBg}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: card.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className={`fa-solid ${card.icon}`} style={{ color: card.color, fontSize: 14 }} />
            </div>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: card.color, marginBottom: 2 }}>{card.value}</div>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{card.label}</div>
        </div>
      ))}
    </div>
  );
};

const TRIP_LABELS = {
  daily_rent: "Daily Rent",
  wedding:    "Wedding",
  picnic:     "Picnic / Tour",
  party:      "Party / Event",
  airport:    "Airport",
  other:      "Other",
};

const VEH_LABELS = {
  car:     "Car",
  van:     "Van",
  bus:     "Bus",
  coaster: "Coaster",
};

const STATUS_BADGE = {
  pending:   "bg-warning text-dark",
  confirmed: "bg-success",
  cancelled: "bg-danger",
  completed: "bg-primary",
};

export const AdminBookings = () => {
  const [loader, setLoader]           = useState(false);
  const [data, setData]               = useState([]);
  const [page, setPage]               = useState(1);
  const [total, setTotal]             = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected]       = useState(null);
  const [location, setLocation]       = useState(null);
  const [vehicles, setVehicles]       = useState([]);
  const [stats, setStats]             = useState({ today: 0, week: 0, month: 0, total: 0 });

  // Detail panel edit state
  const [editAmount, setEditAmount]   = useState("");
  const [editVehicle, setEditVehicle] = useState("");
  const [editStatus, setEditStatus]   = useState("");
  const [saving, setSaving]           = useState(false);

  // Manual add modal
  const [showModal, setShowModal]     = useState(false);
  const [addLoader, setAddLoader]     = useState(false);
  const [manualForm, setManualForm]   = useState({
    name: "", phone: "", email: "",
    tripType: "daily_rent", vehicleType: "van",
    pickupLocation: "", dropLocation: "",
    date: "", departureTime: "", returnTime: "", passengers: "", message: "",
    amount: "", assignedVehicle: "", status: "confirmed",
  });

  const fetchVehicles = async () => {
    try {
      const res = await api.getVehicle();
      if (res?.vehicles) setVehicles(res.vehicles);
    } catch (_) {}
  };

  const getStats = async () => {
    try {
      const res = await api.getBookings({ page: 1, limit: 1000 });
      if (res?.success) {
        const records = res.bookings || [];
        const now = moment();
        const today = records.filter(r => moment(r.createdAt).isSame(now, "day")).length;
        const week = records.filter(r => moment(r.createdAt).isAfter(moment().subtract(7, "days"))).length;
        const month = records.filter(r => moment(r.createdAt).isSame(now, "month")).length;
        setStats({ today, week, month, total: res.total || records.length });
      }
    } catch (_) {}
  };

  const getData = async (pageVal = page, status = statusFilter) => {
    try {
      setLoader(true);
      const response = await api.getBookings({ page: pageVal, limit: LIMIT, status });
      if (response?.success) {
        setData(response.bookings || []);
        setTotal(response.total || 0);
        setTotalPages(response.totalPages || 1);
      }
    } catch {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData(1, "");
    fetchVehicles();
    getStats();
  }, []);

  const handlePageChange = (e, value) => {
    setPage(value);
    getData(value, statusFilter);
  };

  const handleStatusFilter = (e) => {
    const val = e.target.value;
    setStatusFilter(val);
    setPage(1);
    getData(1, val);
  };

  const fetchLocation = async (address) => {
    if (!address) { setLocation({ label: "No address provided" }); return; }
    setLocation({ label: "Fetching location..." });
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address + ", Pakistan")}&format=json&limit=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const d = await res.json();
      if (d.length > 0) {
        setLocation({ label: d[0].display_name, lat: parseFloat(d[0].lat), lon: parseFloat(d[0].lon) });
      } else {
        setLocation({ label: "Location not found" });
      }
    } catch {
      setLocation({ label: "Unable to fetch location" });
    }
  };

  const openDetail = (item) => {
    setSelected(item);
    setLocation(null);
    setEditAmount(item.amount || "");
    setEditVehicle(item.assignedVehicle?._id || "");
    setEditStatus(item.status);
    if (item.lat && item.lon) {
      setLocation({ label: item.pickupLocation, lat: item.lat, lon: item.lon });
    } else {
      fetchLocation(item.pickupLocation);
    }
  };

  const closeDetail = () => { setSelected(null); setLocation(null); };

  const saveChanges = async () => {
    try {
      setSaving(true);
      const res = await api.updateBooking(selected._id, {
        status:          editStatus,
        amount:          editAmount,
        assignedVehicle: editVehicle || null,
      });
      if (res?.success) {
        notify.success("Booking updated!");
        setSelected(res.booking);
        getData(page, statusFilter);
      }
    } catch {
      notify.error("Something went wrong!");
    } finally {
      setSaving(false);
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      const res = await api.deleteBooking(id);
      if (res?.success) {
        notify.success("Booking deleted!");
        if (selected?._id === id) { setSelected(null); setLocation(null); }
        getData(page, statusFilter);
      }
    } catch {
      notify.error("Something went wrong!");
    }
  };

  const onManualChange = (e) =>
    setManualForm({ ...manualForm, [e.target.name]: e.target.value });

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualForm.name || !manualForm.phone || !manualForm.pickupLocation || !manualForm.date) {
      notify.error("Please fill all required fields.");
      return;
    }
    try {
      setAddLoader(true);
      const res = await api.addBookingManual(manualForm);
      if (res?.success) {
        notify.success("Booking added!");
        setShowModal(false);
        setManualForm({
          name: "", phone: "", email: "",
          tripType: "daily_rent", vehicleType: "van",
          pickupLocation: "", dropLocation: "",
          date: "", departureTime: "", returnTime: "", passengers: "", message: "",
          amount: "", assignedVehicle: "", status: "confirmed",
        });
        getData(1, statusFilter);
      }
    } catch (err) {
      notify.error(err?.message || "Something went wrong!");
    } finally {
      setAddLoader(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="DashboardMain">
      <div className="row">
        <div className="col">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h3 className="mb-0">Bookings ({total})</h3>
            <div className="d-flex gap-2 align-items-center">
              <select
                className="form-control"
                style={{ width: 160 }}
                value={statusFilter}
                onChange={handleStatusFilter}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
              <button
                className="btn btn-dark"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => setShowModal(true)}
              >
                <i className="fa-solid fa-plus me-1"></i> Add Manually
              </button>
            </div>
          </div>

          <BookingStats stats={stats} />

          {/* Table + Detail */}
          <div className="d-flex gap-3" style={{ alignItems: "flex-start" }}>
            {/* Table */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {loader ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status" />
                </div>
              ) : (
                <div className="TableMain mt-0">
                  <div className="table-responsive">
                    <table className="table table-hover drivers-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Trip</th>
                          <th>Vehicle</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Source</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.length > 0 ? (
                          data.map((item) => (
                            <tr
                              key={item._id}
                              style={{
                                cursor: "pointer",
                                background: selected?._id === item._id ? "#f0f3ff" : "",
                              }}
                              onClick={() => openDetail(item)}
                            >
                              <td>
                                <div className="table-text-dark fw-600">{item.name}</div>
                                <small className="text-muted">{item.phone}</small>
                              </td>
                              <td>
                                <div className="table-text-dark">
                                  {TRIP_LABELS[item.tripType] || item.tripType}
                                </div>
                                <small className="text-muted">
                                  {VEH_LABELS[item.vehicleType] || item.vehicleType}
                                </small>
                              </td>
                              <td>
                                {item.assignedVehicle?.numberPlate
                                  ? <NumberPlate number={item.assignedVehicle.numberPlate} />
                                  : <span className="text-muted">—</span>}
                              </td>
                              <td>
                                <span className="table-text-dark">
                                  {moment(item.date).format("DD MMM YY")}
                                </span>
                                {item.time && (
                                  <small className="text-muted d-block">{item.time}</small>
                                )}
                              </td>
                              <td>
                                <span className="table-text-dark">
                                  {item.amount > 0 ? `Rs. ${item.amount.toLocaleString()}` : (
                                    <span className="text-muted">—</span>
                                  )}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${STATUS_BADGE[item.status] || "bg-secondary"} text-uppercase`}>
                                  {item.status}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${item.source === "manual" ? "bg-info text-dark" : "bg-light text-dark border"}`}>
                                  {item.source}
                                </span>
                              </td>
                              <td>
                                <div
                                  className="delete p-2 ms-1 cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); deleteBooking(item._id); }}
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center py-4 text-muted">
                              No bookings found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {data.length > 0 && total > LIMIT && (
                <div className="pagination-container">
                  <Stack spacing={2}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Stack>
                </div>
              )}
            </div>

            {/* Detail Panel */}
            {selected && (
              <div
                style={{
                  width: 360,
                  flexShrink: 0,
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  background: "#fff",
                  position: "sticky",
                  top: 20,
                  overflow: "hidden",
                }}
              >
                {/* Panel header */}
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h6 className="mb-0 fw-bold">Booking Detail</h6>
                  <button className="btn btn-sm btn-light" onClick={closeDetail}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>

                {/* Map */}
                <div style={{ height: 180, background: "#f5f5f5", position: "relative" }}>
                  {!location ? (
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                    </div>
                  ) : location.lat ? (
                    <MapContainer
                      key={`${location.lat}-${location.lon}`}
                      center={[location.lat, location.lon]}
                      zoom={13}
                      style={{ height: "100%", width: "100%" }}
                      scrollWheelZoom={false}
                      attributionControl={false}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker position={[location.lat, location.lon]}>
                        <Popup>{location.label}</Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#aaa" }}>
                      <i className="fa-solid fa-map-location-dot" style={{ fontSize: 28, marginBottom: 8 }}></i>
                      <span style={{ fontSize: 12 }}>Map unavailable</span>
                    </div>
                  )}
                  {location?.label && (
                    <div style={{ position: "absolute", bottom: 8, left: 8, right: 8, zIndex: 999, background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                      <i className="fa-solid fa-location-dot text-danger"></i>
                      {location.label}
                    </div>
                  )}
                </div>

                <div style={{ padding: "16px 20px", maxHeight: 560, overflowY: "auto" }}>
                  {/* Info */}
                  <div className="mb-2">
                    <small className="text-muted d-block">Customer</small>
                    <strong>{selected.name}</strong>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">Phone</small>
                    <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                  </div>
                  {selected.email && (
                    <div className="mb-2">
                      <small className="text-muted d-block">Email</small>
                      <a href={`mailto:${selected.email}`}>{selected.email}</a>
                    </div>
                  )}
                  <div className="mb-2">
                    <small className="text-muted d-block">Trip Type</small>
                    <strong>{TRIP_LABELS[selected.tripType] || selected.tripType}</strong>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">Vehicle Type</small>
                    <strong>{VEH_LABELS[selected.vehicleType] || selected.vehicleType}</strong>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">Pickup</small>
                    <strong>{selected.pickupLocation}</strong>
                  </div>
                  {selected.dropLocation && (
                    <div className="mb-2">
                      <small className="text-muted d-block">Drop</small>
                      <strong>{selected.dropLocation}</strong>
                    </div>
                  )}
                  <div className="mb-2">
                    <small className="text-muted d-block">Date</small>
                    <strong>{moment(selected.date).format("DD MMM YYYY")}</strong>
                  </div>
                  <div className="mb-2 d-flex gap-4">
                    <div>
                      <small className="text-muted d-block">Departure time</small>
                      <strong>{selected.departureTime || "—"}</strong>
                    </div>
                    <div>
                      <small className="text-muted d-block">Return time</small>
                      <strong>{selected.returnTime || "—"}</strong>
                    </div>
                  </div>
                  {selected.passengers && (
                    <div className="mb-2">
                      <small className="text-muted d-block">Passengers</small>
                      <strong>{selected.passengers}</strong>
                    </div>
                  )}
                  {selected.message && (
                    <div className="mb-3">
                      <small className="text-muted d-block">Notes</small>
                      <p style={{ whiteSpace: "pre-wrap", marginBottom: 0, fontSize: 14, lineHeight: 1.6 }}>
                        {selected.message}
                      </p>
                    </div>
                  )}
                  <div className="mb-2">
                    <small className="text-muted d-block">Received</small>
                    <span>{moment(selected.createdAt).format("DD MMM YYYY, hh:mm A")}</span>
                  </div>

                  <hr />

                  {/* Admin controls */}
                  <div className="mb-3">
                    <label className="form-label fw-bold" style={{ fontSize: 13 }}>
                      Status
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold" style={{ fontSize: 13 }}>
                      Amount (Rs.)
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder="e.g. 5000"
                      min="0"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold" style={{ fontSize: 13 }}>
                      Assign Vehicle
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={editVehicle}
                      onChange={(e) => setEditVehicle(e.target.value)}
                    >
                      <option value="">— Not assigned —</option>
                      {vehicles.map((v) => (
                        <option key={v._id} value={v._id}>
                          {v.numberPlate} ({v.type})
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    className="btn btn-dark w-100"
                    onClick={saveChanges}
                    disabled={saving}
                  >
                    {saving ? (
                      <><i className="fa-solid fa-spinner fa-spin me-1"></i> Saving...</>
                    ) : (
                      <><i className="fa-solid fa-floppy-disk me-1"></i> Save Changes</>
                    )}
                  </button>

                  <div className="mt-2 d-flex gap-2">
                    <a href={`tel:${selected.phone}`} className="btn btn-sm btn-outline-success w-100">
                      <i className="fa-solid fa-phone me-1"></i> Call
                    </a>
                    {selected.email && (
                      <a href={`mailto:${selected.email}`} className="btn btn-sm btn-outline-primary w-100">
                        <i className="fa-solid fa-envelope me-1"></i> Email
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Manually Modal */}
      {showModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Booking Manually</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={submitManual}>
                  <div className="row g-3">
                    {/* Customer info */}
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Full Name <span className="text-danger">*</span></label>
                      <input name="name" className="form-control" placeholder="Ahsan Iqbal" value={manualForm.name} onChange={onManualChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Phone <span className="text-danger">*</span></label>
                      <input name="phone" className="form-control" placeholder="+92 300 0000000" value={manualForm.phone} onChange={onManualChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Email</label>
                      <input name="email" type="email" className="form-control" placeholder="you@example.com" value={manualForm.email} onChange={onManualChange} />
                    </div>

                    {/* Trip + Vehicle type */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Trip Type <span className="text-danger">*</span></label>
                      <select name="tripType" className="form-control" value={manualForm.tripType} onChange={onManualChange}>
                        <option value="daily_rent">Daily Rent</option>
                        <option value="wedding">Wedding</option>
                        <option value="picnic">Picnic / Tour</option>
                        <option value="party">Party / Event</option>
                        <option value="airport">Airport</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Vehicle Type <span className="text-danger">*</span></label>
                      <select name="vehicleType" className="form-control" value={manualForm.vehicleType} onChange={onManualChange}>
                        <option value="car">Car</option>
                        <option value="van">Van</option>
                        <option value="bus">Bus</option>
                        <option value="coaster">Coaster</option>
                      </select>
                    </div>

                    {/* Location */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Pickup Location <span className="text-danger">*</span></label>
                      <input name="pickupLocation" className="form-control" placeholder="e.g. Gulshan-e-Iqbal, Karachi" value={manualForm.pickupLocation} onChange={onManualChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Drop Location</label>
                      <input name="dropLocation" className="form-control" placeholder="e.g. Hawksbay Beach" value={manualForm.dropLocation} onChange={onManualChange} />
                    </div>

                    {/* Date / Time / Passengers */}
                    <div className="col-md-3">
                      <label className="form-label fw-bold">Date <span className="text-danger">*</span></label>
                      <input name="date" type="date" className="form-control" value={manualForm.date} onChange={onManualChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-bold">Departure time</label>
                      <input name="departureTime" type="time" className="form-control" value={manualForm.departureTime} onChange={onManualChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-bold">Return time</label>
                      <input name="returnTime" type="time" className="form-control" value={manualForm.returnTime} onChange={onManualChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-bold">Passengers</label>
                      <input name="passengers" type="number" min="1" className="form-control" placeholder="e.g. 15" value={manualForm.passengers} onChange={onManualChange} />
                    </div>

                    {/* Admin-only fields */}
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Amount (Rs.)</label>
                      <input name="amount" type="number" min="0" className="form-control" placeholder="e.g. 5000" value={manualForm.amount} onChange={onManualChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Assign Vehicle</label>
                      <select name="assignedVehicle" className="form-control" value={manualForm.assignedVehicle} onChange={onManualChange}>
                        <option value="">— Not assigned —</option>
                        {vehicles.map((v) => (
                          <option key={v._id} value={v._id}>
                            {v.numberPlate} ({v.type})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Status</label>
                      <select name="status" className="form-control" value={manualForm.status} onChange={onManualChange}>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-bold">Additional Notes</label>
                      <textarea name="message" className="form-control" rows={3} placeholder="Special requirements, decorations, route preferences..." value={manualForm.message} onChange={onManualChange} />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-dark" onClick={submitManual} disabled={addLoader}>
                  {addLoader ? (
                    <><i className="fa-solid fa-spinner fa-spin me-1"></i> Adding...</>
                  ) : (
                    <><i className="fa-solid fa-plus me-1"></i> Add Booking</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
